<script setup lang="ts">
import { computed, ComputedRef, ref, watch } from "vue"
import { refDebounced } from "@vueuse/core"
import { Plus, Pencil, Search, MoreVertical } from "lucide-vue-next"
import { useI18n } from "vue-i18n"

import {
  activateConventionUser,
  deactivateConventionUser,
  ConventionUser,
  ExtendedConventionUser,
  confirmConventionUser
} from "@/models/convention_user"
import { ExtendedConvention } from "@/models/convention"
import { createConventionMutation, formatDate, formatDateTime } from "@/lib/utils"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import ConventionUserForm from "@/views/main/ConventionsView/components/ConventionUserForm.vue"
import ConventionUserBadge from "@/views/main/ConventionsView/components/ConventionUserBadge.vue"

const { t } = useI18n()

const props = defineProps<{
  convention: ExtendedConvention
  activeUsersCount: number
}>()

const emits = defineEmits(["create-user-success", "update-user-success", "destroy-user-success"])

const setStatusValue = (value: string) => {
  statusValue.value = value
}

const isStatusValue = (value: string) => {
  return statusValue.value == value
}

const confirmConventionUserMutation = createConventionMutation({
  mutationFn: confirmConventionUser,
  successDescription: t("conventions.toasts.activation_email"),
  errorDescription: t("conventions.toasts.activation_email_error"),
  successCallback: (conventionUser: ConventionUser) => emits("update-user-success", conventionUser)
})

const activateConventionUserMutation = createConventionMutation({
  mutationFn: activateConventionUser,
  successDescription: t("conventions.toasts.user_activation"),
  errorDescription: t("conventions.toasts.user_activation_error"),
  successCallback: (conventionUser: ConventionUser) => emits("update-user-success", conventionUser)
})

const deactivateConventionUserMutation = createConventionMutation({
  mutationFn: deactivateConventionUser,
  successDescription: t("conventions.toasts.user_deactivation"),
  errorDescription: t("conventions.toasts.user_deactivation_error"),
  successCallback: (conventionUser: ConventionUser) => emits("update-user-success", conventionUser)
})

const statusValue = ref("")
const searchValue = ref("")
const debouncedSearch = refDebounced(searchValue, 250)

const filteredConventionUserList: ComputedRef<ExtendedConventionUser[]> = computed(() => {
  let output = []
  const status = statusValue.value
  if (!status) {
    output = props.convention.users || []
  } else {
    output =
      props.convention.users?.filter((item) => {
        return item.status == status
      }) || []
  }

  const searchValue = debouncedSearch.value?.trim()
  if (searchValue) {
    const value_to_search = searchValue.toLowerCase()
    output =
      output.filter((item) => {
        return (
          item.username.toLowerCase().includes(value_to_search) ||
          item.description?.toLowerCase().includes(value_to_search)
        )
      }) || []
  }

  output = output.slice().sort((a, b) => {
    const nameA = a.description || ""
    const nameB = b.description || ""
    return nameA.localeCompare(nameB)
  })

  return output
})

watch(
  () => props.convention,
  (_newValue) => {
    // statusValue.value = newValue.convention_type == "SINGLE_USER" ? "" : "active"
    setStatusValue("")
  },
  { immediate: true }
)

const conventionUsersCountedByStatus = (status: string | null) => {
  let output = []
  if (!status) {
    output = props.convention.users || []
  } else {
    output =
      props.convention.users?.filter((item) => {
        return item.status == status
      }) || []
  }

  return output.length
}

const resendActivationEmail = (username: string) => {
  confirmConventionUserMutation.mutate(username)
}

const activateUser = (username: string) => {
  activateConventionUserMutation.mutate(username)
}

const deactivateUser = (username: string) => {
  deactivateConventionUserMutation.mutate(username)
}

const onCreateSuccess = (conventionUser: ConventionUser) => {
  emits("create-user-success", conventionUser)
}

const onUpdateSuccess = (conventionUser: ConventionUser) => {
  emits("update-user-success", conventionUser)
}

function getDate(date?: string | null, dateTime?: boolean ): string {
  if (!date) return t("conventions.no_date")

  if (dateTime) return formatDateTime(date)

  return formatDate(date)
}

</script>

<template>
  <Card>
    <CardHeader class="flex flex-col items-center justify-between gap-2 lg:flex-row">
      <CardTitle class="flex-0 text-xl font-bold">
        {{ $t("conventions.users") }} -
        <span class="text-sm">
          Max: {{ props.convention.max_users }}
        </span>
      </CardTitle>
      <div
        class="flex w-full flex-1 flex-col items-center gap-2 xl:flex-row"
        v-if="convention.convention_type != 'SINGLE_USER'">
        <div class="flex w-full flex-1 gap-2">
          <Button
            type="button"
            size="sm"
            class="flex-1 items-center"
            :variant="isStatusValue('') ? 'default' : 'outline'"
            @click="setStatusValue('')">
            {{ $t("conventions.user_filters.all") }}
            <span class="ml-3 text-xs">{{ conventionUsersCountedByStatus(null) }}</span>
          </Button>

          <Button
            type="button"
            size="sm"
            class="flex-1 items-center"
            :variant="isStatusValue('active') ? 'default' : 'outline'"
            @click="setStatusValue('active')">
            {{ $t("conventions.user_filters.active") }}
            <span class="ml-3 text-xs">{{ conventionUsersCountedByStatus("active") }}</span>
          </Button>

          <Button
            type="button"
            size="sm"
            class="flex-1 items-center"
            :variant="isStatusValue('pending') ? 'default' : 'outline'"
            @click="setStatusValue('pending')">
            {{ $t("conventions.user_filters.pending") }}
            <span class="ml-3 text-xs">{{ conventionUsersCountedByStatus("pending") }}</span>
          </Button>

          <Button
            type="button"
            size="sm"
            class="flex-1 items-center"
            :variant="isStatusValue('deactivated') ? 'default' : 'outline'"
            @click="setStatusValue('deactivated')">
            {{ $t("conventions.user_filters.deactivated") }}
            <span class="ml-3 text-xs">{{ conventionUsersCountedByStatus("deactivated") }}</span>
          </Button>
        </div>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger as-child>
              <div class="w-full xl:flex-1">
                <ConventionUserForm :convention="convention" @success="onCreateSuccess" v-if="convention">
                  <Button
                    type="button"
                    size="sm"
                    class="w-full xl:flex-1"
                    @click.stop
                    :disabled="(convention?.max_users ?? 1) <= activeUsersCount">
                    <Plus class="cursor-pointer"></Plus>
                    {{ $t("conventions.buttons.add_user") }}
                  </Button>
                </ConventionUserForm>
              </div>
            </TooltipTrigger>

            <TooltipContent v-if="(convention?.max_users ?? 1) <= activeUsersCount">
              {{ $t("conventions.no_registration") }}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </CardHeader>

    <div class="flex-0 px-6 pb-2" v-if="convention.convention_type != 'SINGLE_USER'">
      <div class="relative">
        <Search class="absolute left-2 top-2.5 size-4 text-muted-foreground" />
        <Input v-model="searchValue" :placeholder='`${ $t("conventions.search_placeholder") }`' class="pl-8" />
      </div>
    </div>

    <CardContent>
      <div class="flex flex-col gap-2">
        <Table style="max-height: 400px">
          <TableHeader>
            <TableRow>
              <TableHead>{{ $t("conventions.table_fields.user") }}</TableHead>
              <TableHead class="hidden text-center sm:table-cell">{{ $t("conventions.table_fields.type") }}</TableHead>
              <TableHead class="hidden text-center sm:table-cell">{{ $t("conventions.table_fields.status") }}</TableHead>
              <TableHead class="hidden text-center md:table-cell">{{ $t("conventions.table_fields.creation_date") }}</TableHead>
              <TableHead class="text-center">{{ $t("conventions.table_fields.actions") }}</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody class="overflow-auto">
            <TableRow class="bg-accent" v-if="convention?.users?.length == 0">
              <TableCell class="text-center" colspan="5">{{ $t("conventions.no_users") }}</TableCell>
            </TableRow>

            <TableRow class="bg-accent" v-for="user in filteredConventionUserList" :key="user.id">
              <TableCell>
                <div class="font-medium">{{ user.description?.split("(")[0].trim() }}</div>
                <a
                  :href="`mailto: ${user.username}`"
                  class="text-sm text-muted-foreground hover:text-primary hover:underline">
                  {{ user.username }}
                </a>
              </TableCell>

              <TableCell class="hidden text-center sm:table-cell">
                {{ $t("conventions.roles." + user.roles?.[0]) }}
              </TableCell>

              <TableCell class="hidden text-center sm:table-cell">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <ConventionUserBadge :variant="user.status">
                        {{ $t(`conventions.statuses.${user.status}`) }}
                      </ConventionUserBadge>
                    </TooltipTrigger>

                    <TooltipContent v-if="user.status == 'pending'">
                      {{ $t("conventions.confirmation_sent_at") }}:
                      {{ getDate(user.confirmation_sent_at, true) }}
                    </TooltipContent
                    >
                  </Tooltip>
                </TooltipProvider>
              </TableCell>

              <TableCell class="hidden text-center md:table-cell">
                {{ getDate(user.created_at) }}
              </TableCell>

              <TableCell class="text-center">
                <div class="flex justify-center gap-2">
                  <ConventionUserForm
                    :convention="convention"
                    :conventionUser="user"
                    :role="user.roles?.[0]"
                    @success="onUpdateSuccess">
                    <Button type="button" size="icon" variant="outline" class="h-8 w-8" @click.stop>
                      <Pencil class="h-3 w-3 cursor-pointer"></Pencil>
                    </Button>
                  </ConventionUserForm>

                  <DropdownMenu>
                    <DropdownMenuTrigger as-child>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        class="h-8 w-8"
                        :disabled="user.status == 'active' && user.roles?.includes('admin_convention_users')">
                        <MoreVertical class="h-3.5 w-3.5" />
                        <span class="sr-only">More</span>
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        class="cursor-pointer"
                        v-if="user.status == 'pending'"
                        @click="resendActivationEmail(user.username)">
                        {{ $t("conventions.buttons.resend") }}
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        class="cursor-pointer"
                        v-if="user.status == 'deactivated'"
                        :disabled="activeUsersCount >= (convention.max_users ?? Infinity)"
                        @click="activateUser(user.username)">
                        {{ $t("conventions.buttons.activate") }}
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        class="cursor-pointer"
                        v-if="user.status != 'deactivated' && !user.roles?.includes('admin_convention_users')"
                        @click="deactivateUser(user.username)">
                        {{ $t("conventions.buttons.deactivate") }}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </CardContent>
  </Card>
</template>
